import { useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { requestMagicLink } from "./api";

/** `repos` avant tout envoi, `envoi` pendant, `parti` accepte, `ferme` (503), `echec` pour le reste. */
type SendState = "repos" | "envoi" | "parti" | "ferme" | "echec";

/**
 * L'ecran de connexion (planche « Connexion »), montre a la place de la frise sans session. Une
 * adresse, un lien magique, pas de mot de passe. Le message apres l'envoi est le meme pour une
 * adresse non invitee : l'ecran ne dit jamais qui est admis (copie du modele de Cinepass).
 */
export function LoginScreen() {
  const [address, setAddress] = useState("");
  const [state, setState] = useState<SendState>("repos");
  const id = useId();
  const status = useRef<HTMLDivElement>(null);

  async function send(): Promise<void> {
    setState("envoi");
    try {
      await requestMagicLink(address.trim());
      setState("parti");
    } catch (error) {
      setState(error instanceof ApiError && error.status === 503 ? "ferme" : "echec");
    }
  }

  // Le formulaire laisse place au message : le focus passe au message, que le lecteur d'ecran lit.
  useEffect(() => {
    if (state === "parti") status.current?.focus();
  }, [state]);

  return (
    <section className="mx-auto flex w-full max-w-md flex-col justify-center gap-7 py-6">
      <div className="flex flex-col gap-2.5">
        <h1 className="tl-display m-0 text-[34px]">Ma timeline</h1>
        <p className="m-0 text-base leading-normal text-[#C9D0DE]">
          Un espace privé. On y entre sur invitation, avec un lien envoyé par e-mail, sans mot de
          passe.
        </p>
      </div>

      {state === "parti" ? (
        <div
          className="flex flex-col gap-2 rounded-xl bg-[rgba(243,238,227,0.08)] p-4 focus:outline-none"
          ref={status}
          role="status"
          tabIndex={-1}
        >
          <strong className="text-base">Regarde ta boîte mail.</strong>
          <span className="text-[15px] text-[#C9D0DE]">
            Si cette adresse est invitée, un lien de connexion vient de partir. Pense aux courriers
            indésirables.
          </span>
        </div>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <label className="text-sm font-semibold" htmlFor={`${id}-mail`}>
            Ton adresse e-mail
          </label>
          <input
            autoCapitalize="none"
            autoComplete="email"
            className="min-h-12 rounded-[10px] border border-input bg-tl-field px-3.5 text-base text-foreground placeholder:text-[#8C96AA]"
            id={`${id}-mail`}
            inputMode="email"
            onChange={(event) => setAddress(event.target.value)}
            placeholder="toi@exemple.fr"
            spellCheck={false}
            type="email"
            value={address}
          />
          <button
            className="min-h-12 rounded-[10px] bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
            disabled={state === "envoi" || address.trim() === ""}
            type="submit"
          >
            {state === "envoi" ? "Envoi en cours..." : "Recevoir mon lien"}
          </button>
          {state === "ferme" ? (
            <p className="m-0 text-sm text-destructive" role="alert">
              La connexion est fermée pour le moment. Réessaie plus tard.
            </p>
          ) : null}
          {state === "echec" ? (
            <p className="m-0 text-sm text-destructive" role="alert">
              L'envoi n'a pas abouti. Réessaie dans quelques minutes.
            </p>
          ) : null}
        </form>
      )}
    </section>
  );
}
